import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Sparkles } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { LIVE_ACCOUNT_URL, DEMO_ACCOUNT_URL } from "../lib/api";
import { staggerVariants, VIEWPORT_ONCE_TIGHT } from "../lib/motion";

const accounts = [
    {
        name: "Live Account",
        tag: "Most popular",
        highlight: true,
        price: "From $100",
        priceLabel: "minimum deposit",
        description:
            "Real-money trading with full MT5 access, raw spreads, and segregated funds.",
        features: [
            "Spreads from 0.6 pips on EUR/USD",
            "Up to 1:500 leverage",
            "Forex + Crypto CFDs (100+ instruments)",
            "Negative balance protection",
            "Instant deposits & withdrawals",
            "24/5 priority support",
        ],
        cta: { label: "Open Live Account", href: LIVE_ACCOUNT_URL },
    },
    {
        name: "Demo Account",
        tag: "Risk-free",
        highlight: false,
        price: "$50,000",
        priceLabel: "virtual balance",
        description:
            "Test strategies, learn the platform, and validate edges before going live.",
        features: [
            "Real-time market data feed",
            "Full MT5 functionality",
            "30 days, renewable on request",
            "Same execution model as live",
            "Reset balance anytime",
            "No registration friction",
        ],
        cta: { label: "Open Demo Account", href: DEMO_ACCOUNT_URL },
    },
];

const cardVariants = staggerVariants({ y: 30, duration: 0.6, step: 0.1 });

function AccountHeader({ account }) {
    return (
        <>
            {account.highlight && (
                <div className="absolute top-0 right-0 bg-[#A6E3DC] text-[#050814] text-[10px] font-extrabold tracking-widest uppercase px-4 py-1.5 rounded-bl-lg flex items-center gap-1">
                    <Sparkles size={11} /> {account.tag}
                </div>
            )}
            <p className="overline mb-3">{account.name}</p>
            <div className="flex items-baseline gap-2 mt-1">
                <span className="font-mono text-4xl font-extrabold text-white">
                    {account.price}
                </span>
                <span className="text-slate-500 text-sm">
                    {account.priceLabel}
                </span>
            </div>
            <p className="text-slate-400 mt-4 leading-relaxed">
                {account.description}
            </p>
        </>
    );
}

function AccountFeatureList({ account }) {
    return (
        <ul className="mt-7 space-y-3">
            {account.features.map((f) => (
                <li
                    key={f}
                    className="flex items-start gap-3 text-sm text-slate-300"
                >
                    <span
                        className={`mt-0.5 w-5 h-5 rounded-full grid place-items-center flex-shrink-0 ${
                            account.highlight
                                ? "bg-[#A6E3DC]"
                                : "bg-[#A6E3DC]/15"
                        }`}
                    >
                        <Check
                            size={12}
                            className={
                                account.highlight
                                    ? "text-[#050814]"
                                    : "text-[#A6E3DC]"
                            }
                        />
                    </span>
                    <span>{f}</span>
                </li>
            ))}
        </ul>
    );
}

function AccountCard({ account, index }) {
    const slug = account.name.toLowerCase().replace(" ", "-");
    return (
        <motion.div
            data-testid={`account-card-${slug}`}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            custom={index}
            viewport={VIEWPORT_ONCE_TIGHT}
            className={`relative rounded-2xl p-8 lg:p-10 overflow-hidden transition-all duration-300 ${
                account.highlight
                    ? "bg-gradient-to-b from-[#0A0E23] to-[#050814] border-2 border-[#A6E3DC]/40 shadow-[0_0_60px_rgba(166,227,220,0.15)]"
                    : "xpo-glass"
            }`}
        >
            <AccountHeader account={account} />
            <AccountFeatureList account={account} />
            <a
                href={account.cta.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`account-cta-${slug}`}
                className={`mt-9 w-full justify-center ${
                    account.highlight ? "btn-primary" : "btn-secondary"
                }`}
            >
                {account.cta.label} <ArrowUpRight size={16} />
            </a>
        </motion.div>
    );
}

export default function AccountTypes() {
    return (
        <section
            data-testid="account-types-section"
            className="relative py-24 lg:py-32"
        >
            <div className="xpo-container">
                <SectionHeading
                    overline="Accounts"
                    title="Two paths. One engine. Pick your starting point."
                    description="Whether you're sharpening an edge on demo or scaling a live book, every XPO account runs on the same MT5 infrastructure."
                    align="center"
                />

                <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mt-14 max-w-5xl mx-auto">
                    {accounts.map((a, i) => (
                        <AccountCard key={a.name} account={a} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
